import React, { useEffect, useMemo, useState } from 'react';
import styles from './DoctorSpecialities.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import DeleteDialog from '../../components/DeleteDialog';
import CommonAgGrid from '../../components/CommonAgGrid/CommonAgGrid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  getSpecialities,
  addSpeciality,
  updateSpeciality,
  deleteSpeciality,
} from '../../apis/doctorSpecialitySlice';
import { toast } from 'react-toastify';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { isSuperAdmin } from '../../utils/findUserRole';

export default function DoctorSpecialities() {
  const dispatch = useDispatch();
  const { specialities, loading, totalCount } = useSelector(
    (state) => state.doctorSpecialityData || { specialities: [], loading: false, totalCount: 0 },
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Popup states
  const [openPopup, setOpenPopup] = useState(false);
  const [specialityName, setSpecialityName] = useState('');
  const [editId, setEditId] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  useEffect(() => {
    dispatch(getSpecialities({ page, pageSize }));
  }, [dispatch, page, pageSize]);

  // Open Add popup
  const handleOpenAdd = () => {
    setEditId(null); // reset edit mode
    setSpecialityName(''); // clear input
    setOpenPopup(true);
  };

  // Open Edit popup
  const handleOpenEdit = (row) => {
    setEditId(row._id); // store id for update
    setSpecialityName(row.name);
    setOpenPopup(true);
  };

  // Submit Add / Update
  const handleSubmit = async () => {
    if (!specialityName.trim()) {
      toast.error('Name is required');
      return;
    }

    let response;

    if (editId) {
      // UPDATE
      response = await dispatch(updateSpeciality({ id: editId, name: specialityName }));
    } else {
      // ADD
      response = await dispatch(addSpeciality({ name: specialityName }));
    }

    if (response?.payload?.success) {
      toast.success(response.payload.message);
      setOpenPopup(false); // close popup
      dispatch(getSpecialities({ page, pageSize }));
    } else {
      toast.error(response?.payload?.message || 'Error');
    }
  };

  const handleDelete = async (row) => {
    const response = await dispatch(deleteSpeciality(row._id));

    if (response?.payload?.success) {
      toast.success(response.payload.message);
      dispatch(getSpecialities({ page, pageSize }));
    } else {
      toast.error('Error deleting speciality');
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;
    await handleDelete(deleteRow);
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const handleCancelDelete = () => {
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const columns = useMemo(() => {
    const base = [{ field: 'name', headerName: 'Speciality Name', flex: 1, minWidth: 220 }];
    if (!isSuperAdmin()) return base;
    return [
      ...base,
      {
        colId: 'action',
        headerName: 'Action',
        width: 96,
        flex: 0,
        minWidth: 96,
        maxWidth: 96,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Tooltip title="Edit">
              <IconButton color="primary" size="small" onClick={() => handleOpenEdit(params.data)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton color="error" size="small" onClick={() => handleDeleteClick(params.data)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ];
  }, []);

  const disabled = !specialityName.trim();
  const isInitialLoading = loading && (specialities || []).length === 0;

  return (
    <div className={styles.page}>
      <DeleteDialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        compact
      />
      <Box className={styles.pageHead}>
        <Typography className={styles.pageTitle}>Doctor Specialities</Typography>
      </Box>
      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>Speciality List</h2>
          {isSuperAdmin() && (
            <Button
              className={styles.addBtn}
              variant="contained"
              size="small"
              startIcon={<HealthAndSafetyIcon />}
              onClick={handleOpenAdd}
            >
              Add Speciality
            </Button>
          )}
        </div>

        {isInitialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
            <Typography sx={{ color: '#5d748f' }}>Loading...</Typography>
          </Box>
        ) : (
          <CommonAgGrid
            rowData={specialities || []}
            columnDefs={columns}
            getRowId={(p) => String(p.data._id)}
            loading={loading}
            height={280}
            pagination
            paginationMode="client"
            rowCount={Number(totalCount) || (specialities || []).length}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            pageSizeOptions={[5, 10, 25]}
          />
        )}
      </Card>

      {/* Popup */}
      <Dialog
        open={openPopup}
        fullWidth
        maxWidth="sm"
        onClose={() => setOpenPopup(false)}
        PaperProps={{ sx: { borderRadius: 2, border: '1px solid #d7e2ef' } }}
      >
        <DialogTitle className={styles.dialogTitle}>
          {editId ? 'Edit Doctor Speciality' : 'Add Doctor Speciality'}
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus
            margin="dense"
            size="small"
            label="Speciality Name"
            fullWidth
            value={specialityName}
            onChange={(e) => setSpecialityName(e.target.value)}
            required
          />
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setOpenPopup(false)} variant="outlined" color="inherit" size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            className={styles.addBtn}
            disabled={disabled}
          >
            {editId ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
